import { useCallback, useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { Cookie } from '@/utils/cookie';

import type { TeamTabValue } from './jsm-portal-playground-data';

export const getPageCanonicalUrl = (): string => {
	if (typeof document !== 'undefined') {
		const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
		if (canonical?.href) {
			return canonical.href;
		}
	}
	if (typeof window !== 'undefined') {
		return window.location.href.split('#')[0] ?? '';
	}
	return '';
};

export const getPagePath = (): string => {
	if (typeof window !== 'undefined') {
		return window.location.pathname;
	}
	return '';
};

export const getLoginUserDetected = (): string => {
	if (typeof document !== 'undefined') {
		const cookieVal = Cookie.readCookie('login_user_detected');
		if (cookieVal) {
			return cookieVal;
		}
	}
	return 'false';
};

export const isCustomizeTarget = (target: EventTarget | null): boolean => {
	if (!(target instanceof HTMLElement || target instanceof SVGElement)) {
		return false;
	}

	const element = target as HTMLElement;

	const customizeSelector = [
		'[data-testid="customize-help-center-button"]',
		'[data-testid="jsm-portal-customize-sidebar"]',
		'[data-testid="primary-color-swatch"]',
		'[data-testid="secondary-color-swatch"]',
		'#helpCenterName',
		'[aria-label="Help center name"]',
		'[name="helpCenterName"]',
		'button[aria-label="Customize the help center"]',
		'button[aria-label="Close"]',
	].join(', ');

	if (element.closest(customizeSelector)) {
		return true;
	}

	const text = element.textContent?.trim();
	if (
		text === 'Customize the help center' ||
		text === 'Customize look and feel' ||
		text === 'Change the article banner color' ||
		text === 'Change the article icon color'
	) {
		return true;
	}

	const ariaLabel = element.getAttribute('aria-label');
	if (
		ariaLabel === 'Customize the help center' ||
		ariaLabel === 'Customize look and feel' ||
		ariaLabel === 'Change the article banner color' ||
		ariaLabel === 'Change the article icon color'
	) {
		return true;
	}

	return false;
};

export type UseJsmPlaygroundSectionExploredAnalyticsOptions = {
	initialActiveTab?: TeamTabValue;
};

export const useJsmPlaygroundSectionExploredAnalytics = ({
	initialActiveTab = 'it-support',
}: UseJsmPlaygroundSectionExploredAnalyticsOptions = {}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const sessionStartTimeRef = useRef<number>(Date.now());
	const tabsExploredRef = useRef<TeamTabValue[]>([initialActiveTab]);
	const interactionCountRef = useRef<number>(0);
	const didCustomizeRef = useRef<boolean>(false);
	const hasFiredRef = useRef<boolean>(false);
	const lastPointerDownRef = useRef<{ target: EventTarget | null; time: number } | null>(null);

	const recordTabExplored = useCallback((tab: TeamTabValue) => {
		if (!tabsExploredRef.current.includes(tab)) {
			tabsExploredRef.current.push(tab);
		}
	}, []);

	const checkCustomize = useCallback((target: EventTarget | null) => {
		if (isCustomizeTarget(target)) {
			didCustomizeRef.current = true;
		}
	}, []);

	const recordInteraction = useCallback(() => {
		interactionCountRef.current += 1;
	}, []);

	const handlePointerDown = useCallback(
		(event: { target: EventTarget | null }) => {
			lastPointerDownRef.current = { target: event.target, time: Date.now() };
			recordInteraction();
			checkCustomize(event.target);
		},
		[checkCustomize, recordInteraction],
	);

	const handleClick = useCallback(
		(event: { target: EventTarget | null }) => {
			const now = Date.now();
			const isFromRecentPointerDown =
				lastPointerDownRef.current &&
				now - lastPointerDownRef.current.time < 300 &&
				(lastPointerDownRef.current.target === event.target ||
					(event.target instanceof Node &&
						lastPointerDownRef.current.target instanceof Node &&
						(event.target.contains(lastPointerDownRef.current.target) ||
							lastPointerDownRef.current.target.contains(event.target))));

			if (!isFromRecentPointerDown) {
				recordInteraction();
			}
			checkCustomize(event.target);
		},
		[checkCustomize, recordInteraction],
	);

	const handleKeyDown = useCallback(
		(event: { key: string; target: EventTarget | null }) => {
			if (['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) {
				return;
			}
			recordInteraction();
			checkCustomize(event.target);
		},
		[checkCustomize, recordInteraction],
	);

	const handleChange = useCallback(
		(event: { target: EventTarget | null }) => {
			recordInteraction();
			checkCustomize(event.target);
		},
		[checkCustomize, recordInteraction],
	);

	const fireExploredSummary = useCallback(() => {
		if (hasFiredRef.current) {
			return;
		}
		hasFiredRef.current = true;

		const dwellMs = Math.max(0, Math.round(Date.now() - sessionStartTimeRef.current));
		const tabs = tabsExploredRef.current;

		createAnalyticsEvent({
			actionSubject: 'section',
			action: 'explored',
			actionSubjectId: 'jsmPlayground',
			eventType: 'ui',
			source: 'jsm-playground',
			attributes: {
				trackEventName: 'explored-playground-summary',
				eventContainer: 'jsm-playground',
				tabsExplored: [...tabs],
				tabCountExplored: tabs.length,
				dwellMs,
				interactionCount: interactionCountRef.current,
				didCustomize: didCustomizeRef.current,
				page_canonical_url: getPageCanonicalUrl(),
				page_path: getPagePath(),
				loginUserDetected: getLoginUserDetected(),
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const fireExploredSummaryRef = useRef(fireExploredSummary);
	fireExploredSummaryRef.current = fireExploredSummary;

	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState === 'hidden') {
				fireExploredSummaryRef.current();
			} else if (document.visibilityState === 'visible') {
				// Reset when the visitor returns so subsequent tab switches or page exit fire updated metrics
				hasFiredRef.current = false;
			}
		};

		const handlePageExit = () => {
			fireExploredSummaryRef.current();
		};

		if (typeof document !== 'undefined') {
			document.addEventListener('visibilitychange', handleVisibilityChange);
		}
		if (typeof window !== 'undefined') {
			window.addEventListener('beforeunload', handlePageExit);
			window.addEventListener('pagehide', handlePageExit);
		}

		return () => {
			if (typeof document !== 'undefined') {
				document.removeEventListener('visibilitychange', handleVisibilityChange);
			}
			if (typeof window !== 'undefined') {
				window.removeEventListener('beforeunload', handlePageExit);
				window.removeEventListener('pagehide', handlePageExit);
			}
			fireExploredSummaryRef.current();
		};
	}, []);

	return {
		tabsExploredRef,
		interactionCountRef,
		didCustomizeRef,
		recordTabExplored,
		recordInteraction,
		handlePointerDown,
		handleClick,
		handleKeyDown,
		handleChange,
		fireExploredSummary,
	};
};
